import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
  Selector
} from '@reduxjs/toolkit'
import { query, mutation } from '@polleverywhere/graphql-client'
import * as queries from './activePresenter.queries'
import { NotFoundError } from '../util/NotFoundError'

export interface CustomStyle {
  [index: string]: string | undefined,
  placeholderLogo?: string,
  placeholderTitle?: string,
  placeholderContent?: string,
  mobileTheme?: string,
  javascriptUrl?: string,
  stylesheetUrl?: string
}

export interface SamlLogin {
  [index: string]: string,
  loginLabel: string,
  loginUrl: string
}

export interface RegistrationInfo {
  [index: string]: number | string | boolean | [SamlLogin] | undefined,
  organization: string,
  participationCost: number,
  existingParticipant: boolean,
  participantSelfRegistration: boolean,
  preregistrationMessage?: string,
  firehoseToken?: string,
  samlLogins?: [SamlLogin],
  registrationSuggested: boolean,
  requiresPaidParticipation: boolean,
  screenNamesEnabled: boolean,
  competitionScreenNamesEnabled: boolean
}

export interface Relationship {
  [index: string]: string | undefined,
  id: string,
  screenName?: string
}

export interface ActivePresenter {
  [index: string]: string | boolean | RegistrationInfo | Relationship | CustomStyle | undefined,
  id?: string,
  login: string,
  name: string,
  webBranded: boolean,
  customStyle?: CustomStyle,
  registrationInfo: RegistrationInfo,
  relationship?: Relationship
}

const sliceName = 'activePresenter'

export const selectActivePresenter = (state: any): ActivePresenter => state[sliceName]
export const selectCustomStyle: Selector<unknown, CustomStyle | undefined> = (state) => selectActivePresenter(state)?.customStyle
export const selectRegistrationInfo: Selector<unknown, RegistrationInfo> = (state) => selectActivePresenter(state).registrationInfo
export const selectSelfRegistrationAllowed: Selector<unknown, boolean> = (state) => !!selectRegistrationInfo(state).participantSelfRegistration
export const selectIsExistingParticipant: Selector<unknown, boolean> = (state) => !!selectRegistrationInfo(state).existingParticipant
export const selectParticipationCost: Selector<unknown, number> = (state) => selectRegistrationInfo(state).participationCost
export const selectNeedsToPay: Selector<unknown, boolean> = (state) => selectRegistrationInfo(state).participationCost > 0

export const selectIsRegistered = createSelector(
  selectIsExistingParticipant,
  selectNeedsToPay,
  (isExistingParticipant, needsToPay) => isExistingParticipant && !needsToPay
)

export const selectAccountIdentifier = createSelector(
  (state: any) => selectRegistrationInfo(state).organization,
  (state: any) => selectActivePresenter(state).name,
  (org, fullName) => {
    const accountName = org ? `${org}'s account` : 'their account'
    return `${fullName}, and additional users on ${accountName}, `
  }
)

export const selectHasRelationship: Selector<unknown, boolean> = (state) => !!selectActivePresenter(state).relationship?.id
export const selectScreenName: Selector<unknown, string | undefined> = (state) => selectActivePresenter(state).relationship?.screenName
export const selectHasUnsetScreenName: Selector<unknown, boolean> = (state) => !selectHasRelationship(state) || !selectScreenName(state)

const selectors = {
  selectActivePresenter,
  selectSelfRegistrationAllowed,
  selectIsExistingParticipant,
  selectParticipationCost,
  selectNeedsToPay,
  selectIsRegistered,
  selectAccountIdentifier
}
const thunks = {
  loadPresenter: createAsyncThunk(`${sliceName}/loadPresenter`,
    async (username: string) => {
      return query({
        query: queries.activePresenter,
        variables: { username }
      })
    }
  ),
  refreshRegistrationInfo: createAsyncThunk(`${sliceName}/refreshRegistrationInfo`,
    async (_, { getState }) => {
      return query({
        query: queries.refreshRegistrationInfo,
        variables: {
          username: selectActivePresenter(getState()).login
        }
      })
    }
  ),
  updateScreenName: createAsyncThunk(`${sliceName}/updateScreenName`,
    async (screenName: string | undefined, { getState }) => {
      return mutation({
        query: queries.updateScreenName,
        variables: {
          input: {
            username: selectActivePresenter(getState()).login,
            value: screenName
          }
        }
      })
    }
  )
}
const initialState: ActivePresenter = {
  login: '',
  name: '',
  webBranded: false,
  registrationInfo: {
    organization: '',
    participationCost: 0,
    existingParticipant: false,
    participantSelfRegistration: true,
    registrationSuggested: false,
    requiresPaidParticipation: false,
    screenNamesEnabled: false,
    competitionScreenNamesEnabled: false
  }
}

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset () {
      return initialState
    },
    manuallyUpdated (state, { payload }: PayloadAction<ActivePresenter>) {
      for (const key of Object.keys(payload)) {
        if (key === 'registrationInfo') {
          state[key] = { ...state.registrationInfo, ...payload.registrationInfo }
        } else if (key === 'relationship') {
          if (payload.relationship) {
            state[key] = { ...state.relationship, ...payload.relationship }
          }
        } else if (key === 'customStyle') {
          if (payload.customStyle) {
            state[key] = { ...state.customStyle, ...payload.customStyle }
          }
        } else {
          state[key] = payload[key]
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.loadPresenter.pending, () => {
        return initialState
      })
      .addCase(thunks.loadPresenter.fulfilled, (_, action) => {
        const { data } = action.payload
        return {
          ...data.activityPresenter,
          registrationInfo: { ...data.participantRegistrationInfo },
          relationship: { ...data.participantPresenterRelationship }
        }
      })
      .addCase(thunks.loadPresenter.rejected, (state, action) => {
        console.error('thunks.loadPresenter.rejected', action, state)
        const message = action.error?.message || 'Unknown'
        if (/could not find/.test(message)) {
          throw new NotFoundError(message)
        } else {
          throw new Error(`Unable to load presenter: ${message}`)
        }
      })
    builder
      .addCase(thunks.refreshRegistrationInfo.fulfilled, (state, action) => {
        const { data } = action.payload
        state.registrationInfo = data.participantRegistrationInfo
        state.relationship = data.participantPresenterRelationship
      })
      .addCase(thunks.refreshRegistrationInfo.rejected, (state, action) => {
        console.error('thunks.refreshRegistrationInfo.rejected', action, state)
        // @ts-ignore
        window?.Rollbar?.error('refreshRegistrationInfo rejected', action)
      })
    builder
      .addCase(thunks.updateScreenName.fulfilled, (state, action) => {
        const { data } = action.payload
        state.relationship = data.updateScreenName
      })
      .addCase(thunks.updateScreenName.rejected, (state, action) => {
        console.error('thunks.updateScreenName.rejected', action, state)
        // @ts-ignore
        window?.Rollbar?.error('updateScreenName rejected', action)
      })
  }
})

const sliceExport = {
  name: sliceName,
  reducer: slice.reducer,
  ...slice.actions,
  ...selectors,
  ...thunks
}

export { sliceExport as slice }
